import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

interface BlogCard {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
}

const blogPosts: BlogCard[] = [
  {
    id: 1,
    title: 'Understanding IOR/EOR: A Guide for Global ICT Operations',
    slug: 'understanding-ior-eor-guide-global-it-operations',
    excerpt:
      'Importer and Exporter of Record models are essential for organisations deploying ICT assets across borders. This guide breaks down where responsibility sits and why the structure matters before hardware moves.',
    category: 'Compliance',
    date: 'March 2026',
    readTime: '5 min read',
  },
  {
    id: 2,
    title: 'Why Singapore is the Ideal Hub for ICT Logistics',
    slug: 'why-singapore-ideal-hub-ict-logistics',
    excerpt:
      'Singapore’s infrastructure, regulatory predictability, and regional position make it a strong control point for companies managing global ICT supply chains.',
    category: 'Industry Insights',
    date: 'February 2026',
    readTime: '4 min read',
  },
  {
    id: 3,
    title: 'The Future of ICT Asset Lifecycle Management',
    slug: 'future-it-asset-lifecycle-management',
    excerpt:
      'As global hardware fleets scale, lifecycle control becomes less about tracking a device and more about connecting deployment, service, recovery, and reporting into one model.',
    category: 'ITAM',
    date: 'January 2026',
    readTime: '6 min read',
  },
];

const featuredPost = blogPosts[0];
const remainingPosts = blogPosts.slice(1);

const Blog: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Blog"
        title="Working notes on global ICT logistics."
        highlight="global ICT logistics."
        description="The blog covers market entry, warehouse design, freight control, and lifecycle visibility for teams running hardware programmes across borders."
        heroIcon={DocumentTextIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: `/blog/${featuredPost.slug}`, label: 'Read featured article' },
          { href: '/case-studies', label: 'View case studies', variant: 'secondary' },
        ]}
        badges={blogPosts.map((post) => post.category)}
        metrics={[
          { value: `${blogPosts.length}`, label: 'Published articles' },
          { value: '3', label: 'Core editorial themes' },
          { value: '140+', label: 'Countries in operating scope' },
        ]}
        panelEyebrow="Featured article"
        panelTitle={featuredPost.title}
        panelDescription={featuredPost.excerpt}
        panelItems={[
          {
            icon: ShieldCheckIcon,
            title: featuredPost.category,
            description: `${featuredPost.date} • ${featuredPost.readTime}`,
          },
        ]}
      />

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Featured"
            title="Start with the article most operators ask for first"
            description="The featured piece is a useful first read if you are trying to understand how market entry responsibility works before scaling into new regions."
          />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="rounded-[34px] border border-slate-200/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_30px_80px_-48px_rgba(15,23,42,0.88)] sm:p-9"
          >
            <span className="rounded-full bg-white/[0.08] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
              {featuredPost.category}
            </span>
            <h2 className="mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl">{featuredPost.title}</h2>
            <p className="mt-4 max-w-3xl text-sm leading-8 text-slate-300 sm:text-base">{featuredPost.excerpt}</p>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                {featuredPost.date}
              </span>
              <span className="inline-flex items-center gap-2">
                <ClockIcon className="h-4 w-4" />
                {featuredPost.readTime}
              </span>
            </div>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
            >
              Read article
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Archive"
            title="More articles from the operating layer"
            description="These posts focus on the practical mechanics behind global ICT execution, not generic supply chain commentary."
          />

          <div className="grid gap-6 md:grid-cols-2">
            {remainingPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="rounded-[30px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.28)]"
              >
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  {post.category}
                </span>
                <h2 className="mt-5 text-2xl font-semibold text-slate-950">{post.title}</h2>
                <p className="mt-3 text-sm leading-7 text-slate-600">{post.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {post.date}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <ClockIcon className="h-4 w-4" />
                    {post.readTime}
                  </span>
                </div>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2447d8] transition-colors hover:text-[#0E9ED5]"
                >
                  Read article
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <PageCta
        title="Need insight tied to your own rollout or market-entry plan?"
        description="If the articles are directionally useful but your environment has different countries, timelines, or compliance constraints, we can work through those specifics with you."
        pills={blogPosts.map((post) => post.category)}
        secondaryHref="/resources"
        secondaryLabel="Explore resources"
      />
    </div>
  );
};

export default Blog;
